import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import uk from '../locales/uk.json';
import en from '../locales/en.json';
import { detectLangFromPath, localizePath, stripLangPrefix } from '../utils/i18nPath';

const translations = { uk, en };

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/**
 * Hook to get translation function.
 * Usage: const { t } = useTranslation();
 *        t('nav.home') → "Головна" or "Home"
 */
export const useTranslation = () => {
  const { language } = useLanguage();

  const t = useCallback((key) => {
    const keys = key.split('.');
    const resolve = (lang) => {
      let value = translations[lang];
      for (const segment of keys) {
        value = value?.[segment];
      }
      return value;
    };
    // Try current language first, then fall back to Ukrainian (default),
    // then to the raw key so missing translations are still visible.
    return resolve(language) ?? resolve('uk') ?? key;
  }, [language]);

  return { t };
};

export const LanguageProvider = ({ children }) => {
  // Detect language from the URL on first render so:
  //   • react-snap pre-renders /en/* with English content (SEO).
  //   • Direct visits to /en/prices show English without a flash.
  //   • Server/static HTML matches the client's first paint (no hydration warn).
  const initialLang = (() => {
    if (typeof window === 'undefined') return 'uk';
    return detectLangFromPath(window.location.pathname);
  })();
  const [language, setLanguage] = useState(initialLang);

  // Keep <html lang> in sync with the URL-derived language on first render.
  useEffect(() => {
    document.documentElement.lang = initialLang === 'en' ? 'en' : 'uk';
    // Persist the URL-derived language so future direct visits to "/" still
    // honour the user's last choice (LanguageSwitcher writes here too).
    localStorage.setItem('udent-lang', initialLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for browser back/forward navigation between languages.
  useEffect(() => {
    const onPop = () => {
      const next = detectLangFromPath(window.location.pathname);
      setLanguage(next);
      document.documentElement.lang = next === 'en' ? 'en' : 'uk';
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Switch to the other language by navigating to the equivalent URL on the
  // opposite tree (UA root ↔ /en prefix). Uses History API so React Router
  // picks up the change without a full page reload.
  //
  // Side effects (pushState / localStorage) live OUTSIDE the setState updater
  // so React 18 StrictMode's double-invocation in dev doesn't fire them twice.
  const toggleLanguage = useCallback(() => {
    const next = language === 'uk' ? 'en' : 'uk';
    const base = stripLangPrefix(window.location.pathname);
    const target = localizePath(base, next) + window.location.search + window.location.hash;
    window.history.pushState({}, '', target);
    // Manually fire popstate so React Router re-renders for the new URL.
    window.dispatchEvent(new PopStateEvent('popstate'));
    localStorage.setItem('udent-lang', next);
    document.documentElement.lang = next;
    setLanguage(next);
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    if (!translations[lang] || lang === language) return;
    const base = stripLangPrefix(window.location.pathname);
    const target = localizePath(base, lang) + window.location.search + window.location.hash;
    window.history.pushState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setLanguage(lang);
    localStorage.setItem('udent-lang', lang);
    document.documentElement.lang = lang;
  }, [language]);

  const value = useMemo(
    () => ({ language, toggleLanguage, changeLanguage }),
    [language, toggleLanguage, changeLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
